import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useHistoryStore } from '@/lib/store/history-store';
import { useDeepSearchListener } from '@/lib/hooks/useDeepSearchListener';
import { SocialLinks } from '@/components/search/social-links';
import { Badge } from '@/components/ui/badge';
import type { DeepSearchData } from '@/lib/types';
import { ExternalLink } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { MoreInfoSheet } from '@/components/search/more-info-sheet';
import { logAnalyticsEvent } from '@/lib/firebase/analytics';

export default function SearchPhotoResultPage() {
  const { getSearch, userId } = useHistoryStore();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const hasLoggedComplete = useRef(false);

  // Listen for real-time updates from Firestore
  useDeepSearchListener(userId, id || '');

  useEffect(() => {
    document.title = 'Photo Search Results - Deepcheck';
  }, []);

  // Get uploaded image from sessionStorage
  useEffect(() => {
    if (id) {
      const storedImage = sessionStorage.getItem(`photo-${id}`);
      if (storedImage) {
        setUploadedImage(storedImage);
      }
    }
  }, [id]);

  const search = id ? getSearch(id) : null;
  const result = search?.data as DeepSearchData | undefined;

  // Log search_complete when results are available
  useEffect(() => {
    if (result && search?.status === 'completed' && !hasLoggedComplete.current) {
      logAnalyticsEvent('search_complete', { type: 'photo' });
      hasLoggedComplete.current = true;
    }
  }, [result, search?.status]);

  // Show loading while waiting for data
  if (!search || search.status === 'analyzing' || search.status === 'queued') {
    return (
      <main className="px-4 py-6 flex flex-col items-center justify-center min-h-[60vh]">
        <LoadingSpinner />
        <p className="mt-4 text-gray-600">Analyzing photo...</p>
        {search && <p className="text-sm text-gray-400 mt-2">Progress: {search.progress}%</p>}
      </main>
    );
  }

  // Show error state
  if (search.status === 'failed') {
    return (
      <main className="px-4 py-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-red-800 font-semibold">Search Failed</h2>
          <p className="text-red-600 text-sm mt-1">{search.error?.message || 'An error occurred'}</p>
        </div>
      </main>
    );
  }

  if (!result) {
    return (
      <main className="px-4 py-6">
        <LoadingSpinner />
      </main>
    );
  }

  return (
    <main className="px-4 py-6 space-y-6">
      {/* Header */}
      <h1 className="text-2xl font-bold text-gray-900">{result.title || 'Photo Search'}</h1>

      {/* Uploaded Image Preview */}
      {uploadedImage && (
        <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden">
          <img
            src={uploadedImage}
            alt="Uploaded"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Person Name if identified */}
      {result.name && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-sm text-green-800">
            <span className="font-semibold">Identified: </span>{result.name}
          </p>
        </div>
      )}

      {/* Social Profiles */}
      {result.socialProfiles && result.socialProfiles.length > 0 && (
        <SocialLinks profiles={result.socialProfiles} />
      )}

      {/* Similar Images */}
      {result.images && result.images.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-semibold text-gray-900">Similar Images Found</h2>
          <div className="grid grid-cols-3 gap-2">
            {result.images.slice(0, 9).map((image, index) => (
              <a
                key={index}
                href={image.url}
                target="_blank"
                rel="noopener noreferrer"
                className="aspect-square bg-gray-100 rounded-lg overflow-hidden relative group"
              >
                <img
                  src={image.url}
                  alt={image.caption || `Similar image ${index + 1}`}
                  className="w-full h-full object-cover group-hover:opacity-80 transition-opacity"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                {image.source && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                    {image.source}
                  </div>
                )}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Key Facts */}
      {result.keyFacts && result.keyFacts.length > 0 && (
        <div className="space-y-2">
          <h2 className="font-semibold text-gray-900">Key Facts</h2>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
            {result.keyFacts.map((fact, index) => (
              <li key={index}>{fact}</li>
            ))}
          </ul>
        </div>
      )}

      {/* News Articles */}
      {result.newsArticles && result.newsArticles.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-semibold text-gray-900">Related Articles</h2>
          {result.newsArticles.slice(0, 3).map((article, index) => (
            <a
              key={index}
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white border rounded-lg p-3 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 text-sm">{article.title}</h3>
                  <p className="text-xs text-gray-500 mt-1">{article.source}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Sources Badge */}
      {result.sources && result.sources.length > 0 && (
        <div>
          <MoreInfoSheet sources={result.sources}>
            <Badge variant="secondary" className="text-sm cursor-pointer hover:bg-gray-200 transition-colors">
              {result.sources.length} sources
            </Badge>
          </MoreInfoSheet>
        </div>
      )}

      {/* Summary */}
      <div className="space-y-2">
        <h2 className="font-semibold text-gray-900">Analysis</h2>
        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{result.summary}</p>
      </div>
    </main>
  );
}
